import { NgClass } from "@angular/common";
import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from "@angular/core";
import { RouterLink } from "@angular/router";
import { SearchbarComponent } from "@app/components/searchbar/searchbar.component";
import { isInIframe } from "@app/helpers/iframe-helper";
import { TrackLoginRequestParams, TrackLoginSuccessParams } from "@app/models/tracking/track-event-param-type";
import { AlertDisplayType } from "@commonEnums/AlertDisplayType";
import { delayHelper } from "@commonHelpers/delay-helper";
import { isNullish } from "@commonHelpers/math-utils";
import { TranslatePipe } from "@commonNodeModules/@ngx-translate/core";
import { AlertService } from "@commonServices/alert.service";
import { AlertPositionType } from "@enums/alertPositionType";
import { ViewType } from "@enums/view-type";
import { environment } from "@environments/environment";
import { AccountLanguage } from "@interfaces/HttpClient/AccountApiPublicModels";
import { LoginSource } from "@interfaces/HttpClient/AnalyticsApiTrackingModels";
import { CatalogIntegrationCatalogViewerDto, CatalogIntegrationType } from "@interfaces/HttpClient/CatalogApiPublicModels";
import { AccountService } from "@services/account.service";
import { AnalyticsTrackingFacadeService } from "@services/analytics-tracking-facade.service";
import { CatalogIntegrationSettingsService } from "@services/catalog-integration-settings.service";
import { SoftwareIntegrationCallbackService } from "@services/software-integration-callback.service";
import { SoftwarepartnerApiService } from "@services/softwarepartner-api.service";
import { TranslationService } from "@services/translation.service";
import { ViewTypeService } from "@services/view-type.service";
import { Subscription } from "rxjs";
import { FullscreenHeaderComponent } from "../../components/fullscreen-header/fullscreen-header.component";
import { HelpPublicComponent } from "../../components/help-public/help-public.component";
import { InfoletterHeaderComponent } from "../../components/infoletter-header/infoletter-header.component";
import { LanguageComponent } from "../../components/language/language.component";
import { UserMenuContextComponent } from "../../components/user-menu/user-menu-context/user-menu-context.component";
import { WatchlistHeaderNavigationComponent } from "../../components/watchlist-header-navigation/watchlist-header-navigation.component";
import { HeaderBurgerMenuComponent } from "./header-burger-menu/header-burger-menu.component";

@Component({
	selector: "app-header",
	templateUrl: "./header.component.html",
	styleUrls: ["./header.component.scss"],
	imports: [RouterLink, NgClass, SearchbarComponent, FullscreenHeaderComponent, UserMenuContextComponent, WatchlistHeaderNavigationComponent, HelpPublicComponent, HeaderBurgerMenuComponent, TranslatePipe, LanguageComponent, InfoletterHeaderComponent]
})
export class HeaderComponent implements OnInit, OnChanges, OnDestroy {
	IsNullish = isNullish
	catalogIntegrationTypeEnum = CatalogIntegrationType

	@Input() searchbarIsVisible = false;
	@Input() watchlistIsVisible = true;
	@Input() useOptionalCategoryGroupFilter = false;
	@Output() searchCommit = new EventEmitter();
	@Output() signetClick = new EventEmitter();
	@Input() catalogKey: string;
	@Input() publicCatalogId;
	@Input() applySearchTerm = false;
	@Output() emptySearch = new EventEmitter();
	@Input() title?: string;
	@Input() categoryGroups: { title: string, id: number }[]
	@Input() isPreview = false;
	@Input() catalogIntegrationType: CatalogIntegrationType = CatalogIntegrationType.None;
	@Input() integrationSettings: CatalogIntegrationCatalogViewerDto;
	@Input() theme: "ade" | "neutral" = "neutral"
	@Input() showLanguage = false;
	@Input() noFocusForSearchbar = false;
	@Input() showWatchlist = false;
	@Input() showInfoletter = false;
	@Output() languageChange = new EventEmitter<AccountLanguage>();
	@Output() initSearchbar = new EventEmitter();
	@Input() showWatchlistTitle = false;
	@Input() showInternalWatchlist = false;
	@Input() presentationNavigation = false;
	public canNavigateToStartingPage: boolean
	public canSearchbarBeDisplayed: boolean;
	public currentViewType: ViewType;
	public viewType = ViewType;
	private subscription: Subscription;
	public target: string;
	public isInIntegrationMode = false;
	public hasWatchlist = false;
	public currentLanguage: AccountLanguage;
	public trainingLink = "https://www.wirliebenbau.de/fortbildungen/";

	constructor(
		protected viewTypeService: ViewTypeService,
		private catalogIntegrationSettingsService: CatalogIntegrationSettingsService,
		private alertService: AlertService<AlertPositionType>,
		private translationService: TranslationService,
		private trackingFacadeService: AnalyticsTrackingFacadeService,
		private cdRef: ChangeDetectorRef,
		private accountService: AccountService,
		private softwareIntegrationService: SoftwareIntegrationCallbackService,
		private softwarepartnerApiService: SoftwarepartnerApiService
	) { }


	ngOnInit() {
		this.hasWatchlist = this.showWatchlist && !this.softwareIntegrationService.hasSession() && !this.softwarepartnerApiService.hasSession();
		if (isInIframe()) {
			this.target = '_blank';
			this.accountService.refreshEndInvokeIframe().subscribe(async () => {
				if (this.accountService.isLoggedIn()) {
					this.trackingFacadeService.handleTrackEvent(new TrackLoginSuccessParams())
					await this.trackingFacadeService.commitAllTrackEvents();
				} else {
					if (Object.keys(localStorage).some(key => key.startsWith('msal.1'))) {
						await this.accountService.iframeRefreshLoggedIn();
						if (this.accountService.isLoggedIn()) {
							this.trackingFacadeService.handleTrackEvent(new TrackLoginSuccessParams())
							await this.trackingFacadeService.commitAllTrackEvents();
						}
					}
				}
			}
			);
		}
		this.subscription = this.viewTypeService.currentViewType$.subscribe(async currentViewType => {
			this.currentViewType = currentViewType
			this.updateSearchbar()
		})
		if (!isNullish(this.catalogKey)) {
			this.catalogIntegrationSettingsService.settings$.subscribe(settings => this.canNavigateToStartingPage = settings?.canNavigateToStartingPage)
		} else {
			this.canNavigateToStartingPage = true;
		}

		if (this.integrationSettings?.neutralDesign) {
			this.theme = "neutral";
		}

		this.isInIntegrationMode = !isNullish(this.catalogIntegrationType) && this.catalogIntegrationType !== CatalogIntegrationType.None
	}

	public get disableNavigate() {
		return !this.canNavigateToStartingPage || this.isPreview;
	}

	public showWarningIfPreview() {
		if (this.isPreview) {
			this.alertService.sendMessage(AlertPositionType.Global, AlertDisplayType.Warning, this.translationService.getByKey("preview.notify-message"))
		}
	}

	ngOnDestroy(): void {
		this.subscription?.unsubscribe();
	}

	ngOnChanges(changes: SimpleChanges) {
		if (changes?.searchbarIsVisible?.currentValue !== changes?.searchbarIsVisible?.previousValue) {
			this.updateSearchbar()
		}

		this.isInIntegrationMode = !isNullish(this.catalogIntegrationType) && this.catalogIntegrationType !== CatalogIntegrationType.None
	}

	private updateSearchbar() {
		this.canSearchbarBeDisplayed = this.currentViewType !== ViewType.Desktop ? false : this.searchbarIsVisible
	}

	public async toggleAccount() {
		this.cdRef.detectChanges();
		delayHelper(async () => {
			if (this.accountService.isLoggedIn()) {
				await this.accountService.logout();
			} else {
				this.trackingFacadeService.handleTrackEvent(new TrackLoginRequestParams(LoginSource.UserMenu, this.catalogKey))
				await this.trackingFacadeService.commitAllTrackEvents();
				await this.accountService.login();


			}
		})
	}

	public get accountHandleView() {
		const claims = this.accountService.getAccount()?.idTokenClaims as any
		return this.translationService.getByKey(`catalog-view.thirdpartyviewer.request-login.${this.accountService.isLoggedIn() ? 'logout' : 'title'}`,
			{ name: `${claims?.given_name} ${claims?.family_name}` })
	}

	public get newCustomerLink() {
		return environment.newCustomerUrl
	}

}
